import React from "react";

const PassThroughIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" className="efmd-edge__chip-icon efmd-edge__chip-icon--passthrough" aria-hidden="true">
    <path d="M4 12h14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M13 7l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ScriptIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" className="efmd-edge__chip-icon" aria-hidden="true">
    <path d="M10 6L6 12l4 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 6l4 6-4 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PipelineIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" className="efmd-edge__chip-icon" aria-hidden="true">
    <circle cx="5" cy="12" r="2" fill="currentColor" />
    <circle cx="12" cy="12" r="2" fill="currentColor" />
    <circle cx="19" cy="12" r="2" fill="currentColor" />
    <path d="M7 12h3M14 12h3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const SigmaIcon = () => (
  <span className="efmd-edge__chip-sigma" aria-hidden="true">
    Σ
  </span>
);

const LoopIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" className="efmd-edge__chip-icon" aria-hidden="true">
    <path d="M17 3l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 11V9a2 2 0 0 1 2-2h15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M7 21l-4-4 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 13v2a2 2 0 0 1-2 2H4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const getPath = (sx, sy, tx, ty) => {
  const dx = Math.max(3, Math.abs(tx - sx) * 0.35);
  const direction = tx >= sx ? 1 : -1;
  const c1x = sx + direction * dx;
  const c2x = tx - direction * dx;
  return `M ${sx} ${sy} C ${c1x} ${sy}, ${c2x} ${ty}, ${tx} ${ty}`;
};

const MappingOverlay = ({
  edges,
  layout,
  inputLayout = {},
  selectedEdgeId,
  onSelectEdge,
  onOpenTransform,
  onDeleteEdge,
  isEdgeInvalid,
  aggregateEdges = [],
  aggregateLayout = {},
  aggregateInputLayout = {},
  selectedAggregateId,
  onSelectAggregate,
  onOpenAggregate,
  loopEdges = [],
  loopLayout = {},
  selectedLoopId,
  onSelectLoop,
  onOpenLoop,
  draggingLine,
}) => {
  const orderedEdges = [...edges].sort((a, b) => {
    const aSelected = a.id === selectedEdgeId ? 1 : 0;
    const bSelected = b.id === selectedEdgeId ? 1 : 0;
    return aSelected - bSelected;
  });
  const orderedAggregateEdges = [...aggregateEdges].sort((a, b) => {
    const aSelected = a.id === selectedAggregateId ? 1 : 0;
    const bSelected = b.id === selectedAggregateId ? 1 : 0;
    return aSelected - bSelected;
  });
  const orderedLoopEdges = [...loopEdges].sort((a, b) => {
    const aSelected = a.id === selectedLoopId ? 1 : 0;
    const bSelected = b.id === selectedLoopId ? 1 : 0;
    return aSelected - bSelected;
  });

  return (
    <div className="efmd-overlay">
      <svg className="efmd-overlay__svg">
        {orderedLoopEdges.map((edge, index) => {
          const points = loopLayout[edge.id];
          if (!points) return null;
          const selected = selectedLoopId === edge.id;
          const invalid = edge?.invalid === true;
          const chipTitle = edge?.title || `${edge?.displayName || "Loop"} collection loop`;
          const chipWidth = 2.15 * 16;
          const chipHeight = 1.55 * 16;
          const targets = Array.isArray(points.targets) ? points.targets : [];

          return (
            <g key={`loop:${edge.id}:${index}`}>
              <path
                className={`efmd-edge efmd-edge--loop ${selected ? "is-selected" : ""} ${invalid ? "is-invalid" : ""}`}
                d={getPath(points.sx, points.sy, points.chipX, points.chipY)}
                data-efmd-loop-id={edge.id}
                onClick={() => onSelectLoop?.(edge.id)}
                onDoubleClick={() => onOpenLoop?.(edge.id)}
              />
              {targets.map((target) => (
                <path
                  key={`loop-target:${edge.id}:${target.id}`}
                  className={`efmd-edge efmd-edge--loop ${selected ? "is-selected" : ""} ${invalid ? "is-invalid" : ""}`}
                  d={getPath(points.chipX, points.chipY, target.tx, target.ty)}
                  data-efmd-loop-id={edge.id}
                  data-efmd-target-entity-id={target.id}
                  onClick={() => onSelectLoop?.(edge.id)}
                  onDoubleClick={() => onOpenLoop?.(edge.id)}
                />
              ))}
              <foreignObject
                x={points.chipX - chipWidth / 2}
                y={points.chipY - chipHeight / 2}
                width={chipWidth}
                height={chipHeight}
                data-efmd-loop-id={edge.id}
              >
                <button
                  type="button"
                  className={`efmd-edge__chip efmd-edge__chip--loop ${selected ? "is-selected" : ""} ${invalid ? "is-invalid" : ""}`}
                  title={chipTitle}
                  aria-label={`Edit loop ${chipTitle}`}
                  data-efmd-loop-id={edge.id}
                  onClick={() => {
                    onSelectLoop?.(edge.id);
                    onOpenLoop?.(edge.id);
                  }}
                >
                  <LoopIcon />
                </button>
              </foreignObject>
            </g>
          );
        })}
        {orderedEdges.flatMap((edge) =>
          (inputLayout[edge.id] || []).map((points, index) => {
            const selected = selectedEdgeId === edge.id;
            const invalid = isEdgeInvalid(edge);
            const edgePoints = layout[edge.id];
            const transformX = edgePoints ? (edgePoints.sx + edgePoints.tx) / 2 : points.tx;
            const transformY = edgePoints ? (edgePoints.sy + edgePoints.ty) / 2 : points.ty;
            return (
              <path
                key={`input:${edge.id}:${points.id || index}`}
                className={`efmd-edge efmd-edge--input ${selected ? "is-selected" : ""} ${invalid ? "is-invalid" : ""}`}
                d={getPath(points.sx, points.sy, transformX, transformY)}
                data-efmd-map-edge-id={edge.id}
                data-efmd-target-entity-id={edge.entityFieldId}
                onClick={() => onSelectEdge(edge.id)}
                onDoubleClick={() => onOpenTransform(edge.id)}
              />
            );
          })
        )}
        {orderedEdges.map((edge, index) => {
          const points = layout[edge.id];
          if (!points) return null;
          const selected = selectedEdgeId === edge.id;
          const invalid = isEdgeInvalid(edge);
          const cx = (points.sx + points.tx) / 2;
          const cy = (points.sy + points.ty) / 2;
          const stepCount = Array.isArray(edge?.transformSteps) ? edge.transformSteps.length : 0;
          const isConstant = edge?.isConstant === true;
          const legacyCustomScript = String(
            edge?.legacyCustomTransformScript ||
            edge?.raw?.CustomTransformScript ||
            edge?.raw?.customTransformScript ||
            ""
          ).trim();
          const hasPipeline = stepCount > 0;
          const hasLegacyCustomScript = legacyCustomScript.length > 0 && !hasPipeline;
          const state = hasPipeline ? "pipeline" : hasLegacyCustomScript ? "script" : "empty";
          const chipTitle = isConstant
            ? "Constant mapping"
            : state === "script"
              ? "Single custom transform script is set"
              : state === "pipeline"
                ? "Pipeline transform is set"
                : "Pass through";
          const chipWidth = 2.1 * 16;
          const chipX = isConstant ? points.sx : cx;
          const chipY = isConstant ? points.sy : cy;
          const chipHeight = isConstant ? 1.7 * 16 : 1.55 * 16;
          const chipYOffset = isConstant ? 3 : 0;

          return (
            <g key={`${edge.id}:${index}`}>
              {isConstant ? (
                <path
                  className={`efmd-edge ${selected ? "is-selected" : ""} ${invalid ? "is-invalid" : ""}`}
                  d={getPath(points.sx, points.sy, points.tx, points.ty)}
                  data-efmd-map-edge-id={edge.id}
                  data-efmd-target-entity-id={edge.entityFieldId}
                  onClick={() => onSelectEdge(edge.id)}
                  onDoubleClick={() => onOpenTransform(edge.id)}
                />
              ) : (
                <>
                  <path
                    className={`efmd-edge efmd-edge--input ${selected ? "is-selected" : ""} ${invalid ? "is-invalid" : ""}`}
                    d={getPath(points.sx, points.sy, cx, cy)}
                    data-efmd-map-edge-id={edge.id}
                    data-efmd-target-entity-id={edge.entityFieldId}
                    onClick={() => onSelectEdge(edge.id)}
                    onDoubleClick={() => onOpenTransform(edge.id)}
                  />
                  <path
                    className={`efmd-edge ${selected ? "is-selected" : ""} ${invalid ? "is-invalid" : ""}`}
                    d={getPath(cx, cy, points.tx, points.ty)}
                    data-efmd-map-edge-id={edge.id}
                    data-efmd-target-entity-id={edge.entityFieldId}
                    onClick={() => onSelectEdge(edge.id)}
                    onDoubleClick={() => onOpenTransform(edge.id)}
                  />
                </>
              )}
              <foreignObject
                x={chipX - chipWidth / 2}
                y={chipY - chipHeight / 2 + chipYOffset}
                width={chipWidth}
                height={chipHeight}
                data-efmd-map-edge-id={edge.id}
                data-efmd-target-entity-id={edge.entityFieldId}
              >
                <button
                  type="button"
                  className={`efmd-edge__chip ${selected ? "is-selected" : ""} ${invalid ? "is-invalid" : ""}`}
                  title={chipTitle}
                  aria-label={chipTitle}
                  data-efmd-map-edge-id={edge.id}
                  data-efmd-target-entity-id={edge.entityFieldId}
                  onClick={() => {
                    onSelectEdge(edge.id);
                    onOpenTransform(edge.id);
                  }}
                >
                  {isConstant ? (
                    <span className="efmd-edge__chip-constant">C</span>
                  ) : state === "script" ? (
                    <ScriptIcon />
                  ) : state === "pipeline" ? (
                    <PipelineIcon />
                  ) : (
                    <PassThroughIcon />
                  )}
                </button>
              </foreignObject>
            </g>
          );
        })}
        {orderedAggregateEdges.flatMap((edge) =>
          (aggregateInputLayout[edge.id] || []).map((points, index) => {
            const selected = selectedAggregateId === edge.id;
            const invalid = edge?.invalid === true;
            const aggregatePoints = aggregateLayout[edge.id];
            const reducerX = aggregatePoints ? (aggregatePoints.sx + aggregatePoints.tx) / 2 : points.tx;
            const reducerY = aggregatePoints ? (aggregatePoints.sy + aggregatePoints.ty) / 2 : points.ty;
            return (
              <path
                key={`aggregate-input:${edge.id}:${points.id || index}`}
                className={`efmd-edge efmd-edge--aggregate efmd-edge--input ${selected ? "is-selected" : ""} ${invalid ? "is-invalid" : ""}`}
                d={getPath(points.sx, points.sy, reducerX, reducerY)}
                data-efmd-aggregate-id={edge.id}
                data-efmd-target-entity-id={edge.targetEntityFieldId}
                onClick={() => onSelectAggregate?.(edge.id)}
                onDoubleClick={() => onOpenAggregate?.(edge.id)}
              />
            );
          })
        )}
        {orderedAggregateEdges.map((edge, index) => {
          const points = aggregateLayout[edge.id];
          if (!points) return null;
          const selected = selectedAggregateId === edge.id;
          const invalid = edge?.invalid === true;
          const cx = (points.sx + points.tx) / 2;
          const cy = (points.sy + points.ty) / 2;
          const chipTitle = edge?.title || `${edge?.reducer || "Sum"} aggregate`;
          const chipWidth = 2.15 * 16;
          const chipHeight = 1.55 * 16;

          return (
            <g key={`aggregate:${edge.id}:${index}`}>
              <path
                className={`efmd-edge efmd-edge--aggregate efmd-edge--input ${selected ? "is-selected" : ""} ${invalid ? "is-invalid" : ""}`}
                d={getPath(points.sx, points.sy, cx, cy)}
                data-efmd-aggregate-id={edge.id}
                data-efmd-target-entity-id={edge.targetEntityFieldId}
                onClick={() => onSelectAggregate?.(edge.id)}
                onDoubleClick={() => onOpenAggregate?.(edge.id)}
              />
              <path
                className={`efmd-edge efmd-edge--aggregate ${selected ? "is-selected" : ""} ${invalid ? "is-invalid" : ""}`}
                d={getPath(cx, cy, points.tx, points.ty)}
                data-efmd-aggregate-id={edge.id}
                data-efmd-target-entity-id={edge.targetEntityFieldId}
                onClick={() => onSelectAggregate?.(edge.id)}
                onDoubleClick={() => onOpenAggregate?.(edge.id)}
              />
              <foreignObject
                x={cx - chipWidth / 2}
                y={cy - chipHeight / 2}
                width={chipWidth}
                height={chipHeight}
                data-efmd-aggregate-id={edge.id}
                data-efmd-target-entity-id={edge.targetEntityFieldId}
              >
                <button
                  type="button"
                  className={`efmd-edge__chip efmd-edge__chip--aggregate ${selected ? "is-selected" : ""} ${invalid ? "is-invalid" : ""}`}
                  title={chipTitle}
                  aria-label={`Edit aggregate ${chipTitle}`}
                  data-efmd-aggregate-id={edge.id}
                  data-efmd-target-entity-id={edge.targetEntityFieldId}
                  onClick={() => {
                    onSelectAggregate?.(edge.id);
                    onOpenAggregate?.(edge.id);
                  }}
                >
                  <SigmaIcon />
                </button>
              </foreignObject>
            </g>
          );
        })}
        {draggingLine && (
          <>
            <path className="efmd-edge is-dragging" d={getPath(draggingLine.sx, draggingLine.sy, draggingLine.tx, draggingLine.ty)} />
            {draggingLine.isLoop && (
              <foreignObject
                x={(draggingLine.sx + draggingLine.tx) / 2 - 17}
                y={(draggingLine.sy + draggingLine.ty) / 2 - 12}
                width={34}
                height={24}
              >
                <span className="efmd-edge__chip efmd-edge__chip--loop efmd-edge__chip--dragging">
                  <LoopIcon />
                </span>
              </foreignObject>
            )}
          </>
        )}
      </svg>
    </div>
  );
};

export default MappingOverlay;
